import type { ReactElement, ReactNode } from 'react';
import { Select, type SelectProps } from 'antd';
import { cn } from 'cn';
import type { DefaultSelectSingleProps } from '@/components/types';
import type { FlattenOptionData } from '@/types/antd';

export type BaseSelectOption<Value, Obj> = {
    value: Value;
    label: ReactNode;
    obj: Obj | null;
};

export function BaseSelectSingle<Value, Obj, Params>({
    allowClear = true,
    autoFocus = false,
    className,
    disabled,
    id,
    placeholder,
    ref,
    value,
    onChange,
    onClear,
    onDeselect,
    onSelect,
    popupRender,
    loading,
    options,
    optionRender,
    onPopupScroll,
    showSearch = { filterOption: false },
    open,
    onOpenChange,
    onBlur,
}: Omit<DefaultSelectSingleProps<Value, Obj, Params>, 'onSearch'> & {
    popupRender?: (menu: ReactElement) => ReactElement;
    loading: boolean;
    options: BaseSelectOption<Value, Obj>[];
    optionRender?: (oriOption: FlattenOptionData<BaseSelectOption<Value, Obj>>) => ReactNode;
} & Pick<
        SelectProps<Value, BaseSelectOption<Value, Obj>>,
        'open' | 'onOpenChange' | 'onBlur' | 'showSearch'
    >) {
    const defaultPopupRender = (menu: ReactElement) => {
        if (loading) {
            return <div>Pesquisando...</div>;
        }

        return menu;
    };

    const handleChange = (
        value: Value,
        option?: BaseSelectOption<Value, Obj> | BaseSelectOption<Value, Obj>[],
    ) => {
        if (Array.isArray(option)) {
            return;
        }

        onChange?.(value ?? null, option ? option.obj : null);
    };

    const handleSelect = (_, option: BaseSelectOption<Value, Obj>) => {
        if (option.obj !== null) {
            onSelect?.(option.obj);
        }
    };

    const handleDeselect = (_, option: BaseSelectOption<Value, Obj>) => {
        onDeselect?.(option.value);
    };

    return (
        <Select
            allowClear={allowClear}
            autoFocus={autoFocus}
            className={cn('w-full', className)}
            disabled={disabled}
            id={id}
            loading={loading}
            onBlur={onBlur}
            onChange={handleChange}
            onClear={onClear}
            onDeselect={handleDeselect}
            onOpenChange={onOpenChange}
            onPopupScroll={onPopupScroll}
            onSelect={handleSelect}
            open={open}
            optionRender={optionRender}
            options={options}
            placeholder={placeholder}
            popupRender={popupRender || defaultPopupRender}
            ref={ref}
            showSearch={showSearch}
            value={value}
        />
    );
}
